interface AgentDto {
  agentNumber: string;
  agentName: string | null;
  agentAddress1: string | null;
  agentAddress2: string | null;
  agentCity: string | null;
  agentState: string | null;
  agentZipCode: string | null;
  agentCityStateZip: string | null;
  agentPhoneNumber: string | null;
  agentScorecardGroup: string | null;
  agentContractedYn: string | null;
  agentScorecardContractedYn: string | null;
  agentWebsite: string | null;
  agentStatus: string | null;
}

interface AgencyDto {
  regionName: string | null;
}

interface Props {
  agent: AgentDto;
  agency: AgencyDto | null | undefined;
  actions?: React.ReactNode;
  multiLineAddress?: boolean;
  fieldGridClassName?: string;
}

function buildAddress(agent: AgentDto): string | null {
  const cityStateZip =
    [agent.agentCity, agent.agentState, agent.agentZipCode].filter(Boolean).join(", ") ||
    agent.agentCityStateZip?.replace(",", ", ") ||
    null;
  return [agent.agentAddress1, cityStateZip].filter(Boolean).join(", ") || null;
}

function buildAddressLines(agent: AgentDto): string[] {
  const cityStateZip =
    [agent.agentCity, agent.agentState, agent.agentZipCode].filter(Boolean).join(", ") ||
    agent.agentCityStateZip?.replace(",", ", ") ||
    null;
  // De-dupe Address Line 2 when it matches Line 1 (legacy data sometimes
  // double-fills the same value). Compare case- and whitespace-tolerantly.
  const norm = (s: string | null | undefined) => s?.trim().toLowerCase() ?? "";
  const line2 =
    norm(agent.agentAddress2) === norm(agent.agentAddress1) ? null : agent.agentAddress2;
  return [agent.agentAddress1, line2, cityStateZip].filter((s): s is string => Boolean(s));
}

function Field({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className ?? "min-w-0"}>
      <div className="text-muted-foreground text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 break-words font-medium text-sm">{value ?? "—"}</div>
    </div>
  );
}

export function AgencySummaryCard({
  agent,
  agency,
  actions,
  multiLineAddress = false,
  fieldGridClassName = "grid grid-cols-2 gap-x-4 gap-y-3",
}: Props) {
  const addressLines = buildAddressLines(agent);
  const singleLineAddress = buildAddress(agent);

  return (
    <>
      {actions ? <div className="mb-3 flex flex-wrap gap-1.5">{actions}</div> : null}

      <h2 className="font-bold text-xl">{agent.agentName ?? "—"}</h2>
      {multiLineAddress ? (
        addressLines.length > 0 ? (
          <div className="mt-0.5 text-muted-foreground text-sm">
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ) : (
          <div className="mt-0.5 text-muted-foreground text-sm">No address on file</div>
        )
      ) : (
        <div className="text-muted-foreground text-sm">
          {singleLineAddress ?? "No address on file"}
        </div>
      )}

      <div className={`mt-4 ${fieldGridClassName}`}>
        <Field
          label="Phone"
          value={
            agent.agentPhoneNumber ? (
              <a href={`tel:${agent.agentPhoneNumber}`} className="text-primary hover:underline">
                {agent.agentPhoneNumber}
              </a>
            ) : null
          }
        />
        <Field
          label="Website"
          value={
            agent.agentWebsite ? (
              <a
                href={agent.agentWebsite}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {agent.agentWebsite}
              </a>
            ) : null
          }
        />
        <Field label="Agency No." value={agent.agentNumber} />
        <Field label="Scorecard Group" value={agent.agentScorecardGroup} />
        <Field label="Status" value={agent.agentStatus} />
        <Field label="Region" value={agency?.regionName} />
        <Field label="Contracted?" value={agent.agentContractedYn === "Y" ? "Yes" : "No"} />
        <Field
          label="Scorecard Contracted?"
          value={agent.agentScorecardContractedYn === "Y" ? "Yes" : "No"}
        />
      </div>
    </>
  );
}
